// 일정 관리(v3.26) — 등록/수정 겸용 폼 모달.
// 항목(v1.2 확정): 카테고리 · 분야 · 제목 · 메모 · 시작~종료 일시 · 장소
// 반복 등록(v1.3): 등록 시에만 매일/매주/격주/매월 + 반복 종료 날짜. 수정은 개별 회차만.
import { useEffect, useState } from "react";
import { api, ForbiddenError, UnauthorizedError } from "@/api/client";
import { toast } from "@/lib/toast";
import type { ScheduleItem, ScheduleMeta, ScheduleRepeat } from "@/api/types";
import { localInputToIso, toLocalInputValue } from "./utils";
import { Repeat, X } from "lucide-react";

const REPEAT_OPTIONS: { v: "" | ScheduleRepeat; label: string }[] = [
  { v: "", label: "반복 없음" },
  { v: "daily", label: "매일" },
  { v: "weekly", label: "매주 (같은 요일)" },
  { v: "biweekly", label: "격주" },
  { v: "monthly", label: "매월 (같은 날짜)" },
];

interface Props {
  open: boolean;
  meta: ScheduleMeta | null;
  editing: ScheduleItem | null; // null=등록, 값 있으면 수정(프리필)
  defaultDayKey: string | null; // 캘린더에서 선택한 날짜 — 등록 시 프리필
  onClose: () => void;
  onSaved: () => void; // 저장 성공 → 목록 재조회
}

const inputCls =
  "w-full rounded-lg border border-border bg-card px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40";

function defaultStart(dayKeyStr: string | null): Date {
  // 선택 날짜의 다음 정시(선택 없으면 오늘) — 10:00 같은 깔끔한 기본값
  const base = dayKeyStr ? new Date(`${dayKeyStr}T00:00:00`) : new Date();
  const now = new Date();
  base.setHours(now.getHours() + 1, 0, 0, 0);
  return base;
}

export default function ScheduleFormModal({ open, meta, editing, defaultDayKey, onClose, onSaved }: Props) {
  const [category, setCategory] = useState("");
  const [field, setField] = useState("");
  const [title, setTitle] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [location, setLocation] = useState("");
  const [memo, setMemo] = useState("");
  const [repeat, setRepeat] = useState<"" | ScheduleRepeat>("");
  const [repeatUntil, setRepeatUntil] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 열릴 때 프리필(수정=기존값 / 등록=기본값)
  useEffect(() => {
    if (!open) return;
    setErr(null);
    if (editing) {
      setCategory(editing.category);
      setField(editing.field);
      setTitle(editing.title);
      setStartAt(toLocalInputValue(new Date(editing.start_at)));
      setEndAt(toLocalInputValue(new Date(editing.end_at)));
      setLocation(editing.location ?? "");
      setMemo(editing.memo ?? "");
      setRepeat("");
      setRepeatUntil("");
    } else {
      const st = defaultStart(defaultDayKey);
      const en = new Date(st.getTime() + 60 * 60 * 1000);
      setCategory(meta?.categories[0] ?? "모임");
      setField(meta?.fields[0] ?? "공통");
      setTitle("");
      setStartAt(toLocalInputValue(st));
      setEndAt(toLocalInputValue(en));
      setLocation("");
      setMemo("");
      setRepeat("");
      setRepeatUntil("");
    }
  }, [open, editing, defaultDayKey, meta]);

  if (!open) return null;

  // 시작 변경 시 종료가 시작 이전이면 +1시간 자동 제안
  const onStartChange = (v: string) => {
    setStartAt(v);
    if (!endAt || new Date(endAt) <= new Date(v)) {
      setEndAt(toLocalInputValue(new Date(new Date(v).getTime() + 60 * 60 * 1000)));
    }
  };

  const submit = async () => {
    if (saving) return;
    setErr(null);
    if (!title.trim()) { setErr("제목을 입력해 주세요."); return; }
    if (!startAt || !endAt) { setErr("시작/종료 일시를 입력해 주세요."); return; }
    if (new Date(endAt) <= new Date(startAt)) {
      setErr("종료 일시는 시작 일시보다 뒤여야 합니다.");
      return;
    }
    if (!editing && repeat) {
      if (!repeatUntil) { setErr("반복 종료 날짜를 선택해 주세요."); return; }
      const startDay = startAt.slice(0, 10);
      if (repeatUntil < startDay) { setErr("반복 종료 날짜는 시작 일시 이후여야 합니다."); return; }
      const limit = new Date(startDay);
      limit.setFullYear(limit.getFullYear() + 1);
      if (new Date(repeatUntil) > limit) { setErr("반복은 시작일로부터 최대 1년까지 가능합니다."); return; }
    }
    const body = {
      category,
      field,
      title: title.trim(),
      start_at: localInputToIso(startAt),
      end_at: localInputToIso(endAt),
      location: location.trim(),
      memo: memo.trim(),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.scheduleUpdate(editing.id, body);
        toast("일정을 수정했습니다.", "success");
      } else {
        const created = await api.scheduleCreate(
          repeat ? { ...body, repeat, repeat_until: repeatUntil } : body,
        );
        toast(
          created.created_count > 1
            ? `반복 일정 ${created.created_count}건을 등록했습니다.`
            : "일정을 등록했습니다.",
          "success",
        );
      }
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof UnauthorizedError) setErr("로그인이 필요합니다.");
      else if (e instanceof ForbiddenError) setErr(e.message);
      else setErr(e instanceof Error ? e.message : "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-card p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <span className="font-semibold">{editing ? "일정 수정" : "일정 등록"}</span>
          <button onClick={onClose} aria-label="닫기" className="rounded-md p-1 text-muted-foreground hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                카테고리 <span className="text-destructive">*</span>
              </span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                {(meta?.categories ?? ["모임", "회의", "기타"]).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                분야 <span className="text-destructive">*</span>
              </span>
              <select value={field} onChange={(e) => setField(e.target.value)} className={inputCls}>
                {(meta?.fields ?? ["공통"]).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">
              제목 <span className="text-destructive">*</span>
            </span>
            <input
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 찬양팀 Zoom 회의"
              className={inputCls}
            />
          </label>

          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                시작 일시 <span className="text-destructive">*</span>
              </span>
              <input
                type="datetime-local"
                value={startAt}
                step={600}
                onChange={(e) => onStartChange(e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                종료 일시 <span className="text-destructive">*</span>
              </span>
              <input
                type="datetime-local"
                value={endAt}
                step={600}
                onChange={(e) => setEndAt(e.target.value)}
                className={inputCls}
              />
            </label>
          </div>

          {/* 반복 등록(v1.3) — 등록 시에만. 수정은 개별 회차만 적용(확정 사항) */}
          {editing ? (
            editing.repeat_group && (
              <p className="flex items-center gap-1 rounded-lg bg-muted px-2.5 py-2 text-xs text-muted-foreground">
                <Repeat className="h-3.5 w-3.5 shrink-0" />
                반복 일정의 한 회차입니다 — 수정은 이 회차에만 적용됩니다.
              </p>
            )
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold text-muted-foreground">반복</span>
                <select
                  value={repeat}
                  onChange={(e) => setRepeat(e.target.value as "" | ScheduleRepeat)}
                  className={inputCls}
                >
                  {REPEAT_OPTIONS.map((o) => (
                    <option key={o.v} value={o.v}>{o.label}</option>
                  ))}
                </select>
              </label>
              {repeat && (
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-muted-foreground">
                    반복 종료 날짜 <span className="text-destructive">*</span>
                  </span>
                  <input
                    type="date"
                    value={repeatUntil}
                    min={startAt.slice(0, 10)}
                    onChange={(e) => setRepeatUntil(e.target.value)}
                    className={inputCls}
                  />
                  <span className="mt-0.5 block text-[11px] text-muted-foreground">
                    이 날짜까지 반복(포함) · 최대 1년
                  </span>
                </label>
              )}
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">장소</span>
            <input
              value={location}
              maxLength={200}
              onChange={(e) => setLocation(e.target.value)}
              placeholder='예: "Zoom(라이선스1)", "본관 2층 회의실"'
              className={inputCls}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-muted-foreground">메모</span>
            <textarea
              value={memo}
              maxLength={2000}
              rows={3}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="Zoom 링크, 안건 등 자유 기재"
              className={inputCls + " resize-y"}
            />
          </label>

          {err && <p className="text-xs text-destructive">{err}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted"
            >
              취소
            </button>
            <button
              onClick={submit}
              disabled={saving}
              className="rounded-lg bg-[#0E1E2D] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1C3954] disabled:opacity-50"
            >
              {saving ? "저장 중…" : "저장"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
